import React from 'react';
import { Icon, Tooltip } from '@weezevent/nacre';

import HTMLReactParser, { attributesToProps } from 'html-react-parser';
import AddBlock from './addBlock';
import Styles from '../../styles/minisite/blocks.module.css';
import { Trans, useTranslation } from 'react-i18next';
import DateBlock from './customBlocks/dateBlock';
import LocationBlock from './customBlocks/locationBlock';
import ContactBlock from './customBlocks/contactBlock';
import DescriptionBlock from './customBlocks/descriptionBlock';
import CTABlock from './ctaBlock';
import WidgetBlock from './customBlocks/widgetBlock';
import BannerBlock from './customBlocks/bannerBlock';

export const NOT_EDITABLE_BLOCK = [
    'image-header',
    'event-description',
    'date-and-location',
    'socials-share',
    'location-light',
    'date-light',
    'event-name',
    'date',
    'location',
    'organization',
    'perso-css',
    'language-dropdown'
];

export function getBlock(blocks, name) {
    if (!blocks) {
        return null;
    }
    return blocks.find(block => block.get('type') === name) || null;
}

export function findBlockById(blocks, id) {
    return blocks.find(block => block.get('id') === id) || null;
}

const FacebookPlaceholder = () => (
    <div className={Styles['block-facebook']}>
        <div className={Styles['block-facebook-icon']}>
            <Icon name="facebook" />
        </div>
        <div>
            <Trans i18nKey="ticket.event.minisite.facebook-block-title" />
        </div>
        <div className={Styles['block-facebook-subtitle']}>
            <Trans i18nKey="ticket.event.minisite.facebook-block-placeholder" />
        </div>
    </div>
);
const AddNewBlock = ({ bottom = false, onClick }) => {
    const { t } = useTranslation();
    return (
        <span className={`${Styles['block-plus']} ${bottom ? Styles['block-minus'] : ''}`}>
            <Tooltip mini triggerOnHover title={t('ticket.event.minisite.block.tooltip')} position="top">
                <span className={Styles['icon-trigger']} onClick={onClick}>
                    <Icon name="plus" margin={false} />
                </span>
            </Tooltip>
        </span>
    );
};

const BlocksAdditionSuggestions = ({ name, blockPresence, organization, handleSubmit, event, createBlock, blocks }) => {
    // Define block types and their conditions for rendering
    const blockTypes = [
        { name: 'header', isPresent: blockPresence['image-header'] || blockPresence['event-image-header'], component: BannerBlock, blockType: '' },
        { name: 'column-left', isPresent: blockPresence['date-light'], component: DateBlock, blockType: 'date-light' },
        { name: 'column-left', isPresent: blockPresence['location-light'], component: LocationBlock, blockType: 'location-light' },
        { name: 'cta-event', isPresent: blockPresence['cta'], component: CTABlock, blockType: 'cta' },
        { name: 'column-left', isPresent: blockPresence['event-contact'], component: ContactBlock, blockType: 'event-contact' },
        { name: 'column-left', isPresent: blockPresence['event-description'], component: DescriptionBlock, blockType: 'event-description' },
        { name: 'column-left', isPresent: blockPresence['event-widget'], component: WidgetBlock, blockType: 'event-widget' }
    ];

    // If the block's name matches the provided name prop and the block is not already present on the page
    // (as indicated by isPresent), a BlockComponent of that type is rendered.
    return blockTypes.map(
        ({ name: blockName, isPresent, component: BlockComponent, blockType }) =>
            name === blockName &&
            !isPresent && (
                <BlockComponent
                    key={blockType}
                    data=""
                    onClick={() => createBlock(blockType)}
                    organization={organization}
                    handleSubmit={handleSubmit}
                    event={event}
                    blocks={blocks}
                />
            )
    );
};

export function RenderBlocks({ blocks, event, name, onDelete, onEdit, createBlock, handlePosition, organization, handleSubmit, selectPosition }) {
    const { t } = useTranslation();
    const [asyncValues, setAsyncValues] = React.useState(null);

    const { blockList, blockPresence } = React.useMemo(() => {
        // Filter and sort the blocks for the current place
        const sortedBlocks = blocks.filter(block => block && block.place === name).sort((a, b) => a.position - b.position);

        // Create an object to track the presence of each block type
        const presence = sortedBlocks.reduce((acc, block) => {
            acc[block.type] = true;
            return acc;
        }, {});

        return { blockList: sortedBlocks, blockPresence: presence };
    }, [blocks, asyncValues]);

    const _handlePosition = React.useCallback(
        (block, step, index) => {
            handlePosition(block, step).then(error => {
                if (error) {
                    setAsyncValues(blockList);
                }
            });
            const newData = [...blockList];
            const currentPos = block.position;
            const nextPos = block.position + step;

            const blockToReplace = newData.findIndex(block => block.position === nextPos);
            newData[index].position = nextPos;

            // if a block already have this new position, just exchange positions
            if (blockToReplace !== -1) {
                newData[blockToReplace].position = currentPos;
            }
            setAsyncValues(newData);
        },
        [blockList, setAsyncValues]
    );

    const orderedData = blockList;

    return (
        <>
            {orderedData.map((block, index) => {
                let iframe = '';
                if (block.type === 'event-widget') {
                    let props = {};
                    let title = '';
                    HTMLReactParser(block.data, {
                        replace: node => {
                            if (node.attribs && node.name === 'a') {
                                props = attributesToProps(node.attribs);
                                props['data-src'] = `${props['data-src']}#weez_preview`;
                            }
                            if (node.children && node.name === 'h2') {
                                title = node.children[0].data;
                            }
                        }
                    });
                    iframe = `
                        <div id="gemino-buy">
                            <div class="gemino-card-body gemino-card-wiget">
                                <h2 class="gemino-card-title">
                                ${title}
                                </h2>
                                <div class="gemino-ticketing-container">
                                    <iframe src=${props['data-src']} height=${props['data-height']} resize="1" width="100%" frameborder="0" allowfullscreen="true" scrolling="auto" hidepoweredby="1" style="height: ${props['data-heigth']};"></iframe>
                                </div>
                            </div>
                        </div>
                    `;
                }
                const isEditable = !NOT_EDITABLE_BLOCK.includes(block.type);
                if (name === 'perso-css') {
                    return HTMLReactParser(block.data);
                }
                if (block.type === 'event-description') {
                    return (
                        <div key={`block-${index}`} className={`${Styles['block-editable']} ${Styles['block-date-and-location']}`}>
                            <span className={Styles['block-editable-delete']}>
                                <span className={Styles['block-editable-cta']} onClick={() => onDelete(block)}>
                                    <Icon name="trash" />
                                </span>
                                <span className={Styles['block-editable-ordering']}>
                                    {block.position < orderedData.length - 1 && (
                                        <Tooltip mini triggerOnHover title={<Trans i18nKey="ticket.event.minisite.position.down" />}>
                                            <span className={Styles['block-editable-cta']} onClick={() => _handlePosition(block, 1, index)}>
                                                <Icon name="arrow-bottom" />
                                            </span>
                                        </Tooltip>
                                    )}
                                    {block.position > 0 && (
                                        <Tooltip mini triggerOnHover title={<Trans i18nKey="ticket.event.minisite.position.up" />}>
                                            <span className={Styles['block-editable-cta']} onClick={() => _handlePosition(block, -1, index)}>
                                                <Icon name="arrow-top" />
                                            </span>
                                        </Tooltip>
                                    )}
                                </span>
                            </span>
                            <AddNewBlock onClick={() => selectPosition(block.position, name)} />
                            <AddNewBlock bottom={true} onClick={() => selectPosition(block.position + 1, name)} />
                            <DescriptionBlock key={`block-${index}`} event={event} block={block} />
                        </div>
                    );
                }
                if (block.type === 'cta') {
                    return <CTABlock key={`block-${index}`} data={block.data} onEdit={() => onEdit(block)} onDelete={() => onDelete(block)} blocks={blocks} />;
                }
                if (block.type === 'image-header' || block.type === 'event-image-header') {
                    return (
                        <BannerBlock
                            key={`block-${index}`}
                            data={block.data}
                            organization={organization}
                            event={event}
                            handleSubmit={handleSubmit}
                            handleDelete={() => onDelete(block)}
                        />
                    );
                }

                if (['date-and-location', 'location-light', 'date-light', 'event-contact', 'event-description', 'facebook', 'socials-share'].includes(block.type)) {
                    return (
                        <div key={`block-${index}`} className={`${Styles['block-editable']} ${Styles['block-date-and-location']}`}>
                            <span className={Styles['block-editable-delete']}>
                                <span className={Styles['block-editable-cta']} onClick={() => onDelete(block)}>
                                    <Icon name="trash" />
                                </span>
                                <span className={Styles['block-editable-ordering']}>
                                    {block.position < orderedData.length - 1 && (
                                        <Tooltip mini triggerOnHover title={<Trans i18nKey="ticket.event.minisite.position.down" />}>
                                            <span className={Styles['block-editable-cta']} onClick={() => _handlePosition(block, 1, index)}>
                                                <Icon name="arrow-bottom" />
                                            </span>
                                        </Tooltip>
                                    )}
                                    {block.position > 0 && (
                                        <Tooltip mini triggerOnHover title={<Trans i18nKey="ticket.event.minisite.position.up" />}>
                                            <span className={Styles['block-editable-cta']} onClick={() => _handlePosition(block, -1, index)}>
                                                <Icon name="arrow-top" />
                                            </span>
                                        </Tooltip>
                                    )}
                                </span>
                            </span>
                            <AddNewBlock onClick={() => selectPosition(block.position, name)} />
                            <AddNewBlock bottom={true} onClick={() => selectPosition(block.position + 1, name)} />
                            {block.type === 'facebook' ? <FacebookPlaceholder /> : HTMLReactParser(block.data)}
                        </div>
                    );
                }

                return (
                    <div key={`block-${index}`} className={isEditable ? Styles['block-editable'] : Styles['block-not-editable']}>
                        {isEditable && (
                            <>
                                <span className={Styles['block-editable-delete']}>
                                    <span className={Styles['block-editable-cta']} onClick={() => onEdit(block)}>
                                        <Icon name="edit" /> {t('ticket.common.action.edit')}
                                    </span>
                                    <span className={Styles['block-editable-cta']} onClick={() => onDelete(block)}>
                                        <Icon name="trash" />
                                    </span>
                                    <span className={Styles['block-editable-ordering']}>
                                        {block.position < orderedData.length - 1 && (
                                            <Tooltip mini triggerOnHover title={<Trans i18nKey="ticket.event.minisite.position.down" />}>
                                                <span className={Styles['block-editable-cta']} onClick={() => _handlePosition(block, 1, index)}>
                                                    <Icon name="arrow-bottom" />
                                                </span>
                                            </Tooltip>
                                        )}
                                        {block.position > 0 && (
                                            <Tooltip mini triggerOnHover title={<Trans i18nKey="ticket.event.minisite.position.up" />}>
                                                <span className={Styles['block-editable-cta']} onClick={() => _handlePosition(block, -1, index)}>
                                                    <Icon name="arrow-top" />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </span>
                                </span>
                                <AddNewBlock onClick={() => selectPosition(block.position, name)} />
                                <AddNewBlock bottom={true} onClick={() => selectPosition(block.position + 1, name)} />
                            </>
                        )}
                        {HTMLReactParser(block.type === 'event-widget' ? iframe : block.data)}
                    </div>
                );
            })}
            <BlocksAdditionSuggestions
                name={name}
                blockPresence={blockPresence}
                organization={organization}
                handleSubmit={handleSubmit}
                event={event}
                createBlock={createBlock}
                blocks={blocks}
            />
        </>
    );
}

export function Template({ html, event, selectPlace, blocks, onDelete, onEdit, createBlock, organization, handleSubmit, selectPosition, handlePosition }) {
    React.useEffect(() => {
        const script = document.createElement('script');

        script.src = 'https://widget.weezevent.com/weez.js';
        script.async = true;

        document.body.appendChild(script);

        // Find Weez-base.js
        let match = html.match(/([a-zA-Z0-9.:/]+)\/_static\/weezevent-base-([a-zA-Z0-9]+)\.js/i);
        const scriptBase = document.createElement('script');
        const path = match[0];

        if (match) {
            scriptBase.src = path;
            scriptBase.async = true;
            scriptBase.onload = () => {
                window.postMessage({ type: 'WZ_READY' }, '*');
            };
            document.body.appendChild(scriptBase);
        }

        return () => {
            document.body.removeChild(script);
            document.body.removeChild(scriptBase);
        };
    }, []);

    //send token to Neo fpr preveiw widget
    React.useEffect(() => {
        const listener = ev => {
            if (ev.origin !== window.weezConfig.api.widgetUrl) {
                return;
            }
            const { type, origin, id } = ev.data;
            if (type !== 'request_token' && origin !== 'neo') {
                return;
            }

            window.removeEventListener('message', listener, false);
            ev.source.postMessage({ id, type: 'answer_token', origin: 'willy', payload: { token: localStorage.weezeventAuthToken } }, '*');
        };

        window.addEventListener('message', listener);
        return () => {
            window.removeEventListener('message', listener);
        };
    }, []);

    return HTMLReactParser(html, {
        replace: domNode => {
            if (domNode.type === 'script' && domNode.attribs && domNode.attribs.type === 'weez-place') {
                let data = JSON.parse(domNode.children[0].data);
                if (data.name === 'column-left' || data.name === 'column-right') {
                    return (
                        <>
                            <RenderBlocks
                                organization={organization}
                                event={event}
                                blocks={blocks}
                                name={data.name}
                                onDelete={onDelete}
                                onEdit={onEdit}
                                createBlock={createBlock}
                                selectPosition={selectPosition}
                                handleSubmit={handleSubmit}
                                handlePosition={handlePosition}
                            />
                            <AddBlock onClick={() => selectPlace(data.name)} />
                        </>
                    );
                } else {
                    return (
                        <RenderBlocks
                            organization={organization}
                            event={event}
                            blocks={blocks}
                            name={data.name}
                            onDelete={onDelete}
                            onEdit={onEdit}
                            handleSubmit={handleSubmit}
                            createBlock={createBlock}
                            handlePosition={handlePosition}
                            selectPosition={selectPosition}
                        />
                    );
                }
            }
        }
    });
}
